/**
 * WP-10 — Abonnement (Mollie), see PLAN-FASE1.md § Abonnement. Keys marked
 * `// legal` carry text lifted from the API's own OpenAPI descriptions
 * (§7.5 immediate-performance consent, the art. 6:230oa BW withdrawal
 * right) and must not be reworded without checking the API source again —
 * WP-15's translation pass covers polish, not these.
 */
export const subscription = {
  title: 'Subscription',
  lead: 'Manage your own subscription — plan, payment and cancellation.',
  dialogCancel: 'Cancel',
  status: {
    title: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    tier: 'Plan',
    expires: 'Expires',
    autoRenews: 'Renews automatically',
    doesNotRenew: 'Does not renew',
    nextCharge: 'Next charge',
    blocked: 'You cannot start a web subscription right now.',
  },
  plans: {
    title: 'Plans',
    monthly: 'Monthly',
    yearly: 'Yearly',
    perMonth: '{{price}} / month',
    perYear: '{{price}} / year',
    subscribe: 'Subscribe',
    current: 'Current plan',
    switchToYearly: 'Switch to yearly',
    switchToMonthly: 'Switch to monthly',
    // legal — Wet van Dam / product decision: the overlap between the old
    // and new plan can be charged twice, so switching needs an explicit
    // confirmation, not a silent action.
    switchWarning:
      'Switching cancels your current subscription and starts the new one immediately on the same payment mandate. The overlap between the two periods is not credited back — you may be charged twice for it.',
    switchConfirm: 'Yes, switch plan',
    blocked: 'You already have an active subscription, so no other plan can be started right now.',
  },
  manage: {
    title: 'Manage',
    cancel: 'Cancel subscription',
    cancelConfirm: 'Yes, cancel',
    cancelled: 'Cancelled — access remains until {{date}}.',
    // legal — mirrors the API's own pro-rata description for the yearly
    // plan past the first term (§7.2, Wet van Dam).
    proRataNote:
      'On the yearly plan, cancelling after the first payment term refunds the unused whole months and ends access immediately instead of at the end of the period.',
    withdraw: 'Withdraw (within 14 days)',
    withdrawHint: 'Available for 14 days after your first payment, once, per subscription.',
  },
  history: {
    title: 'History',
    empty: 'No purchases yet.',
    date: 'Date',
    period: 'Period',
    channel: 'Channel',
    state: 'Status',
    state_granted: 'Granted',
    state_refunded: 'Refunded',
    state_revoked: 'Revoked',
    state_charged_back: 'Charged back',
    state_rejected: 'Rejected',
    invoice: 'Invoice',
    withdrawLink: 'Withdraw',
    manageLink: 'Manage in store',
  },
  checkout: {
    title: 'Checkout',
    lead: 'Review your plan before continuing to Mollie.',
    plan: 'Plan',
    price: 'Price',
    // legal — §7.5 immediate-performance consent, literal text from the
    // API-beschrijving in PLAN-FASE1.md; the API refuses the checkout with
    // `consent_required` without this box checked.
    consentLabel:
      'I want the service to start immediately and understand that, if I withdraw, I pay pro rata for the part already used.',
    start: 'Continue to Mollie',
    errorAlreadySubscribed: 'You already have an active subscription.',
    errorProductUnavailable: 'This plan is not available right now.',
  },
  return: {
    title: 'Payment status',
    waiting: "We're still waiting for the payment confirmation.",
    success: 'Payment received — your subscription is active.',
    backToSubscription: 'Back to your subscription',
  },
  withdraw: {
    title: 'Withdraw your subscription',
    // legal — art. 6:230oa BW herroepingsrecht: the API distinguishes a
    // full refund (no immediate-performance consent given) from a pro-rata
    // refund (consent given), see `SubscriptionWithdrawalPreview.refund_type`.
    lead: 'Withdrawing under your 14-day right of withdrawal ends your access immediately — unlike cancelling, which runs until the end of the period you already paid for.',
    refundTypeFull: 'You will receive a full refund.',
    refundTypeProRata: 'You will receive a pro-rata refund for the part not yet used.',
    refundAmount: 'Refund amount',
    deadline: 'Deadline',
    confirm: 'Withdraw and end access now',
    confirmed: 'Withdrawn — {{amount}} will be refunded.',
    errorWindowExpired: 'The 14-day withdrawal window has passed.',
    errorAlreadyWithdrawn: 'You have already withdrawn from this subscription.',
  },
} as const;
